/**
 * CartCrossSell – „Passt gut dazu"-Rolle im Warenkorb-Drawer.
 *
 * Fragt aggregiert Empfehlungen zu allen Cart-Items ab und rendert sie
 * als kompakte Kacheln mit 1-Klick-Hinzufügen. Rendert nichts, wenn der
 * Cart leer ist oder keine Empfehlungen zurückkommen.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCart, formatEUR } from './CartContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CartCrossSell({ cartItems }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(null);
  const { addToCart, setCartOpen } = useCart();
  const navigate = useNavigate();

  const productIds = React.useMemo(
    () => (cartItems || []).map((it) => it.product_id).filter(Boolean),
    [cartItems]
  );
  const key = productIds.slice().sort().join(',');

  useEffect(() => {
    if (productIds.length === 0) { setItems([]); return; }
    let cancelled = false;
    axios
      .post(`${API}/api/shop/cart-cross-sell`, { product_ids: productIds }, { params: { limit: 4 } })
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [key, productIds]);

  /**
   * Fügt Cross-Sell-Produkt hinzu. Bei Produkten mit Varianten-Stock
   * (Farbe/Größe) kann das Backend OUT_OF_STOCK zurückgeben, weil wir keine
   * konkrete Variante kennen — dann leiten wir sanft auf die PDP weiter,
   * damit der Kunde selbst Farbe/Größe wählen kann.
   */
  const handleAdd = async (p) => {
    try {
      setAdding(p.id);
      const price = Number(p.base_price || 0);
      await addToCart({
        product_id: p.id,
        product_name: p.name,
        quantity: 1,
        variant: { color: '', size: '', without_embroidery: false },
        unit_price_cents: price,
        line_total_cents: price,
      });
      // Track Cross-Sell-Conversion — für das Cross-Sell-Dashboard.
      // Best-Effort: ohne Sub-Shop-Kontext nicht messbar, deshalb wird
      // der Slug aus dem aktuellen Sub-Shop ermittelt.
      axios.post(`${API}/api/shop/sub-shops/cross-sell-track`, {
        product_id: p.id,
        source: 'cart',
      }).catch(() => {});
      toast.success(`„${p.name}" hinzugefügt`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === 'object' && detail !== null ? detail.code : null;
      if (code === 'OUT_OF_STOCK' || e?.response?.status === 409) {
        toast.message(`„${p.name}" hat Varianten — bitte Farbe/Größe wählen.`);
        setCartOpen(false);
        navigate(`/shop/${p.slug}`);
      } else {
        // Struktur-Detail (Objekt) sicher zu String konvertieren
        const msg =
          typeof detail === 'string'
            ? detail
            : (detail && (detail.message || detail.msg))
            || 'Konnte nicht hinzufügen';
        toast.error(msg);
      }
    } finally {
      setAdding(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="mt-6 pt-4 border-t border-white/40" data-testid="cart-cross-sell">
      <div className="text-xs uppercase tracking-wider text-[#8a6d10] mb-2">Passt gut dazu</div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((p) => (
          <div
            key={p.id}
            className="bg-white/70 rounded-lg overflow-hidden flex items-center gap-2 p-2"
            data-testid={`cart-cross-sell-item-${p.slug}`}
          >
            {p.image_url && (
              <img
                src={p.image_url}
                alt={p.name}
                loading="lazy"
                className="w-12 h-12 rounded object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium leading-tight line-clamp-2">{p.name}</div>
              <div className="text-xs font-bold text-[#8a6d10] mt-0.5">{formatEUR(p.base_price)}</div>
            </div>
            <button
              type="button"
              onClick={() => handleAdd(p)}
              disabled={adding === p.id}
              className="w-8 h-8 rounded-full bg-[#C9A23C] hover:bg-[#b38f2e] text-[#171512] flex items-center justify-center flex-shrink-0 disabled:opacity-50"
              aria-label="Hinzufügen"
              data-testid={`cart-cross-sell-add-${p.slug}`}
            >
              {adding === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
